import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Confirm, Icons, Modal } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import ProfileTabSection from '@components/common/ProfileTabSection';
import Table from '@components/common/Table';
import EditNetworkElementsForm from '@components/routes/AdminPanel/ManageNetworkElements/EditNetworkElementsForm';

import { useDragAndDropState } from '@hooks/common/useDragAndDropState';
import useMutateNetworkElement from '@hooks/networkElements/useMutateNetworkElement';
import { useNetworkElementsConnectionLogsFilters } from '@hooks/networkElements/useNetworkElementsConnectionLogsFilters';
import { useNetworkElementsFilters } from '@hooks/networkElements/useNetworkElementsFilters';
import useQueryConnectionLogs from '@hooks/networkElements/useQueryConnectionLogs';
import useQueryNetworkElements from '@hooks/networkElements/useQueryNetworkElements';
import { useWindowHeight } from '@hooks/utils/useWindowHeight';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import {
	INetworkElementDTO,
	INetworkElementsSFTPPathsDTO,
	TNetworkElementAutoSwitchDTO,
} from '@interfaces/networkElements/dto/networkElements';
import { INetworkElement } from '@interfaces/networkElements/networkElements';

import { defaultMeta, EAdminPanelPageTitles } from '@enums/common';

import colors from '@constants/colors';
import { WINDOW_HEIGHT_THRESHOLD } from '@constants/common';
import { MODAL_WIDTH_580 } from '@constants/modals';
import {
	AUTOSWITCH_DISABLE_SFTP_SUBTEXT,
	CONNECT_TEXT, DELETE_TEXT,
	DISABLE_AUTOSWITCH_TEXT,
	DISABLE_SFTP_TEXT,
	DISCONNECT_TEXT,
	NETWORK_ELEMENTS_TABS_NAMES,
	SUBSCRIBE_SFTP_AUTOSWITCH_SUBTEXT,
	SUBSCRIBE_SFTP_SUBTEXT,
	SUBSCRIBE_SFTP_TEXT,
	SUBSCRIBE_TEXT,
	UNSUBSCRIBE_SFTP_TEXT,
	UNSUBSCRIBE_TEXT,
} from './constants';

import { getRowItemId } from 'components/common/Table/helpers';

import { getAlarmFilterValues } from '../AlarmManagement/AlarmManagementByVendor/utils';
import { generateAlarmsFilters } from '../Alarms/AlarmByVendor/utils';
import NetworkElementsAutoSwitchForm from './NetworkElementsAutoSwitchForm';
import NetworkElementsForm from './NetworkElementsForm';
import NetworkElementsSFTPForm from './NetworkElementsSFTPForm';
import {
	getConnectionLogsColumns,
	getNetworkElementsColumns,
	getNetworkElementsTabs,
} from './utils';

import styles from './ManageNetworkElements.module.scss';

const tabs = getNetworkElementsTabs();

const defaultState = {
	page: defaultMeta.page,
	pageSize: defaultMeta.pageSize,
	sortDirection: false,
	sortFieldName: null,
	searchValue: '',
	filter: {},
};

const defaultTableState = {
	[NETWORK_ELEMENTS_TABS_NAMES.networkElements]: defaultState,
	[NETWORK_ELEMENTS_TABS_NAMES.connectionStatusLog]: defaultState,
};

interface ITableState {
	[key: string]: {
		page: number;
		pageSize: number;
		sortDirection: boolean;
		sortFieldName: string | null;
		searchValue: string;
		filter: Record<string, any>;
	};
}

const ManageNetworkElements = () => {
	const {
		accessPermission: {
			smoLightUpdater,
			smoLightViewer,
		},
		activeOrganisation: organizationId,
	} = useAppSelector(auth);

	const [currentTab, setCurrentTab] = useState(NETWORK_ELEMENTS_TABS_NAMES.networkElements);
	const [selectedNetworkElement, setSelectedNetworkElement] = useState<INetworkElement | undefined>();

	const [isDirtyForm, setIsDirtyForm] = useState(false);
	const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
	const [isOpenSFTPModal, setIsOpenSFTPModal] = useState(false);
	const [isOpenAutoSwitchModal, setIsOpenAutoSwitchModal] = useState(false);
	const [isOpenNetworkElementModal, setIsOpenNetworkElementModal] = useState(false);
	const [confirmationText, setConfirmationText] = useState('');

	const [tableState, setTableState] = useState<ITableState>(defaultTableState);

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	} = tableState[currentTab];

	const isNetworkElementsTab = currentTab === NETWORK_ELEMENTS_TABS_NAMES.networkElements;

	const { filtersList } = useNetworkElementsFilters();
	const {
		connectionLogsFiltersList,
	} = useNetworkElementsConnectionLogsFilters({ organizationId });

	const filterValues = useMemo(() => {
		return getAlarmFilterValues(tableState[currentTab].filter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableState[currentTab].filter]);

	const {
		networkElements,
		isLoadingNetworkElements,
		countOfRecords: networkElementsCountOfRecords,
		allNetworkElements,
	} = useQueryNetworkElements({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		isEnabled: isNetworkElementsTab,
		organizationId,
		...filterValues,
	});

	const {
		connectionLogs,
		isLoadingConnectionLogs,
		countOfRecords: connectionLogsCountOfRecords,
	} = useQueryConnectionLogs({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		isEnabled: !isNetworkElementsTab,
		organizationId,
		...filterValues,
	});

	const countOfRecords = isNetworkElementsTab
		? networkElementsCountOfRecords
		: connectionLogsCountOfRecords;

	const {
		reorderedData: reorderedNetworkElements,
		onRowReorder: handleNetworkElementsReorder,
		resetOrder: resetNetworkElementsOrder,
		hasCustomOrder: hasCustomNetworkElementsOrder,
	} = useDragAndDropState<INetworkElement>({
		allData: allNetworkElements,
		currentPageData: networkElements,
		page,
		pageSize,
		storageKey: 'networkElements-table-order',
		getItemId: getRowItemId,
		isEnabled: isNetworkElementsTab,
	});

	const openNetworkElementModal = useCallback((networkElement?: INetworkElement) => {
		if (networkElement) setSelectedNetworkElement(networkElement);

		setIsOpenNetworkElementModal((prevState) => !prevState);
	}, []);

	const closeNetworkElementModal = useCallback(() => {
		setIsOpenNetworkElementModal(false);
		setSelectedNetworkElement(undefined);
	}, []);

	const openConfirmModal = useCallback((networkElement: INetworkElement | null, text: string) => {
		if (!networkElement) {
			return;
		}

		setIsOpenConfirmModal((prevState) => !prevState);
		setConfirmationText(text);
		setSelectedNetworkElement(networkElement);
	}, []);

	const closeConfirmModal = useCallback(() => {
		setIsOpenConfirmModal(false);
		setSelectedNetworkElement(undefined);
	}, []);

	const openSFTPModal = useCallback((networkElement?: INetworkElement) => {
		if (!networkElement) {
			return;
		}

		setSelectedNetworkElement(networkElement);
		setIsOpenSFTPModal((prevState) => !prevState);
	}, []);

	const closeSFTPModal = useCallback(() => {
		setIsOpenSFTPModal(false);
	}, []);

	const openAutoSwitchModal = useCallback((networkElement?: INetworkElement) => {
		if (!networkElement) {
			return;
		}

		setSelectedNetworkElement(networkElement);
		setIsOpenAutoSwitchModal(true);
	}, []);

	const closeAutoSwitchModal = useCallback(() => {
		setIsOpenAutoSwitchModal(false);
	}, []);

	const {
		connectNetworkElement,
		deleteNetworkElement,
		subscribeNetworkElement,
		editNetworkElement,
		createNetworkElement,
		enableNetworkElementAutoSwitch,
		disableNetworkElementAutoSwitch,
		handleCheckName,
		handleCheckIpAddress,
		handleDisableSFTP,
		handleEnableSFTP,
		isLoadingNetworkElementMutation,
	} = useMutateNetworkElement({
		closeConfirmModal,
		closeNetworkElementModal,
		closeSFTPModal,
		closeAutoSwitchModal,
	});

	const isViewer = !smoLightUpdater && smoLightViewer;

	const isLoadingConfirmModal = isLoadingNetworkElementMutation;

	const isLoadingTable = isLoadingConnectionLogs || isLoadingNetworkElements;

	const submitNetworkElement = useCallback((dto: INetworkElementDTO) => {
		if (!dto) return;

		if (selectedNetworkElement) {
			return editNetworkElement(dto);
		}

		createNetworkElement(dto);
	}, [
		createNetworkElement,
		editNetworkElement,
		selectedNetworkElement,
	]);

	const onEditNetworkElement = useCallback((dto: INetworkElementDTO) => {
		editNetworkElement(dto);
	}, [editNetworkElement]);

	const toggleNetworkElementConnection = useCallback((networkElement: INetworkElement) => {
		const { id, enabled } = networkElement;

		connectNetworkElement({ id, enabled: !enabled });
	}, [connectNetworkElement]);

	const toggleNetworkElementSubscription = useCallback(async (networkElement: INetworkElement) => {
		const { id, subscription, autoSwitch } = networkElement;

		if (subscription && autoSwitch) {
			await disableNetworkElementAutoSwitch({ id });
		}

		subscribeNetworkElement({ id, subscription: !subscription });
	}, [disableNetworkElementAutoSwitch, subscribeNetworkElement]);

	const removeHandler = useCallback((networkElement: INetworkElement) => {
		if (!networkElement) {
			return;
		}

		deleteNetworkElement(networkElement.id);
	}, [deleteNetworkElement]);

	const disableSFTPHandler = useCallback(async (networkElement: INetworkElement) => {
		if (!networkElement) {
			return;
		}

		await disableNetworkElementAutoSwitch({ id: networkElement.id });
		handleDisableSFTP({ id: networkElement.id });
	}, [disableNetworkElementAutoSwitch, handleDisableSFTP]);

	const enableSFTPNetworkElement = useCallback(({ id, ...dto }: INetworkElementsSFTPPathsDTO) => {
		if (!id) return;

		handleEnableSFTP({ id, ...dto });
	}, [handleEnableSFTP]);

	const disableAutoSwitchHandler = useCallback((networkElement: INetworkElement) => {
		if (!networkElement) {
			return;
		}

		disableNetworkElementAutoSwitch({ id: networkElement.id });
	}, [disableNetworkElementAutoSwitch]);

	const enableAutoSwitchNetworkElement = useCallback(({ id, ...dto }: TNetworkElementAutoSwitchDTO) => {
		if (!id) {
			return;
		}

		enableNetworkElementAutoSwitch({ id, ...dto });
	}, [enableNetworkElementAutoSwitch]);

	const getSubText = useCallback(() => {
		switch (confirmationText) {
			case UNSUBSCRIBE_TEXT:
				return selectedNetworkElement?.autoSwitch ? AUTOSWITCH_DISABLE_SFTP_SUBTEXT : null;
			case SUBSCRIBE_SFTP_TEXT:
				return selectedNetworkElement?.autoSwitch
					? SUBSCRIBE_SFTP_AUTOSWITCH_SUBTEXT
					: SUBSCRIBE_SFTP_SUBTEXT;
			case UNSUBSCRIBE_SFTP_TEXT:
			case DISABLE_SFTP_TEXT:
				return selectedNetworkElement?.autoSwitch
					? AUTOSWITCH_DISABLE_SFTP_SUBTEXT
					: null;
			default:
				return null;
		}
	}, [confirmationText, selectedNetworkElement]);

	const subTextConfirmation = getSubText();

	const submitConfirmHandler = useCallback(() => {
		if (!selectedNetworkElement || !confirmationText) return;

		switch (confirmationText) {
			case DELETE_TEXT:
				return removeHandler(selectedNetworkElement);
			case DISABLE_SFTP_TEXT:
				return disableSFTPHandler(selectedNetworkElement);
			case DISABLE_AUTOSWITCH_TEXT:
				return disableAutoSwitchHandler(selectedNetworkElement);
			case CONNECT_TEXT:
			case DISCONNECT_TEXT:
				return toggleNetworkElementConnection(selectedNetworkElement);
			case SUBSCRIBE_TEXT:
			case SUBSCRIBE_SFTP_TEXT:
			case UNSUBSCRIBE_TEXT:
				if (subTextConfirmation === SUBSCRIBE_SFTP_AUTOSWITCH_SUBTEXT) {
					disableAutoSwitchHandler(selectedNetworkElement);
					disableSFTPHandler(selectedNetworkElement);
				}

				return toggleNetworkElementSubscription(selectedNetworkElement);
			default:
				return null;
		}
	}, [
		selectedNetworkElement,
		confirmationText,
		removeHandler,
		disableSFTPHandler,
		disableAutoSwitchHandler,
		toggleNetworkElementConnection,
		subTextConfirmation,
		toggleNetworkElementSubscription,
	]);

	const columns = useMemo(() => {
		return isNetworkElementsTab
			? getNetworkElementsColumns({
				openConfirmModal,
				openNetworkElementModal,
				openSFTPModal,
				openAutoSwitchModal,
				isViewer,
			})
			: getConnectionLogsColumns();
	}, [
		openConfirmModal,
		openNetworkElementModal,
		openSFTPModal,
		openAutoSwitchModal,
		isNetworkElementsTab,
		isViewer,
	]);

	const setFilters = useCallback((value: Record<string, TSelectOption>) => {
		setTableState((prevState) => {
				return {
					...prevState,
					[currentTab]: {
						...prevState[currentTab],
						filter: {
							...prevState[currentTab].filter,
							...value,
						},
					},
				};
			},
		);
	}, [currentTab, setTableState]);

	const searchHandler = useCallback((searchValue: string) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				searchValue,
				page: defaultMeta.page,
			},
		}));

	}, [setTableState, currentTab]);

	const pageHandler = useCallback((page: number) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				page,
			},
		}));
	}, [currentTab]);

	const pageSizeHandler = useCallback((pageSize: number) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				pageSize,
				page: defaultMeta.page,
			},
		}));
	}, [currentTab]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				sortDirection: sortBy[0]?.desc || false,
				sortFieldName: sortBy[0]?.id,
			},
		}));
	}, [currentTab]);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
		resetOrder: resetNetworkElementsOrder,
		hasCustomOrder: hasCustomNetworkElementsOrder && isNetworkElementsTab,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
		resetNetworkElementsOrder,
		hasCustomNetworkElementsOrder,
		isNetworkElementsTab,
	]);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: isNetworkElementsTab
			? 'Search by name, port and middleware'
			: 'Search by name and event type',
	}], [searchValue, searchHandler, isNetworkElementsTab]);

	const filters: TTableFilter[] = useMemo(() => {
		return generateAlarmsFilters(
			isNetworkElementsTab ? filtersList : connectionLogsFiltersList,
			tableState[currentTab].filter,
			setFilters,
		);
	}, [
		isNetworkElementsTab,
		filtersList,
		connectionLogsFiltersList,
		currentTab,
		tableState,
		setFilters,
	]);

	const windowHeight = useWindowHeight();

	const [tableMaxHeight, setTableMaxHeight] = useState('unset');

	useEffect(() => {
		setTableMaxHeight(windowHeight < WINDOW_HEIGHT_THRESHOLD ? '68vh' : '79vh');
	}, [windowHeight]);

	const getCurrentTab = useCallback(() => {
		switch (currentTab) {
			case NETWORK_ELEMENTS_TABS_NAMES.networkElements:
				return (
					<Table
						key={NETWORK_ELEMENTS_TABS_NAMES.networkElements}
						columns={columns}
						data={reorderedNetworkElements}
						dragHandle
						filters={filters}
						getRowId={getRowItemId}
						isCreatable={smoLightUpdater}
						isDragAndDrop={countOfRecords > 1}
						isLoading={isLoadingTable}
						isOpenSearch={!!searchValue}
						isPinColumns
						isWithoutCheckbox
						isWithoutDeleteAction
						maxHeight={tableMaxHeight}
						onRowReorder={handleNetworkElementsReorder}
						pagination={pagination}
						search={search}
						searchThreshold={0}
						sort={sort}
						stickyLeftColumnIndexes={[0, 1, 2, 3, 4]}
						toggleAddModal={openNetworkElementModal}
					/>
				);
			case NETWORK_ELEMENTS_TABS_NAMES.connectionStatusLog:
				return (
					<Table
						key={NETWORK_ELEMENTS_TABS_NAMES.connectionStatusLog}
						columns={columns}
						data={connectionLogs}
						filters={filters}
						isLoading={isLoadingTable}
						isOpenSearch={!!searchValue}
						isWithoutCheckbox
						isWithoutDeleteAction
						maxHeight={tableMaxHeight}
						pagination={pagination}
						search={search}
						searchThreshold={0}
						sort={sort}
					/>
				);
			default:
				return null;
		}
	}, [
		currentTab,
		columns,
		reorderedNetworkElements,
		filters,
		smoLightUpdater,
		countOfRecords,
		isLoadingTable,
		searchValue,
		tableMaxHeight,
		pagination,
		search,
		sort,
		openNetworkElementModal,
		handleNetworkElementsReorder,
		connectionLogs,
	]);

	return (
		<div className={styles.wrapper}>
			<PageTitle
				icon={Icons.middlewareSMO({ isActive: true, color: colors.white })}
				title={EAdminPanelPageTitles.NetworkElements}
			/>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyForm}
				isLoading={isLoadingConfirmModal}
				isOpen={isOpenAutoSwitchModal}
				onClose={closeAutoSwitchModal}
				title='Enable auto switch'
				width={MODAL_WIDTH_580}
			>
				<NetworkElementsAutoSwitchForm
					id={selectedNetworkElement?.id as string}
					isLoadingSubmit={isLoadingConfirmModal}
					networkElement={selectedNetworkElement}
					onClose={closeAutoSwitchModal}
					onSubmit={enableAutoSwitchNetworkElement}
					setIsDirtyForm={setIsDirtyForm}
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyForm}
				isLoading={isLoadingConfirmModal}
				isOpen={isOpenSFTPModal}
				onClose={closeSFTPModal}
				width={MODAL_WIDTH_580}
			>
				<NetworkElementsSFTPForm
					id={selectedNetworkElement?.id as string}
					isLoadingSubmit={isLoadingConfirmModal}
					networkElement={selectedNetworkElement}
					onClose={closeSFTPModal}
					onSubmit={enableSFTPNetworkElement}
					setIsDirtyForm={setIsDirtyForm}
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyForm}
				isLoading={isLoadingConfirmModal}
				isOpen={isOpenNetworkElementModal}
				onClose={closeNetworkElementModal}
				title='Add network element'
				width={MODAL_WIDTH_580}
			>
				<NetworkElementsForm
					handleCheckIpAddress={handleCheckIpAddress}
					handleCheckName={handleCheckName}
					isLoadingForm={isLoadingConfirmModal}
					networkElement={selectedNetworkElement as INetworkElement}
					setIsDirtyForm={setIsDirtyForm}
					submitHandler={submitNetworkElement}
				/>
			</Modal>

			<Modal
				isConfirmDialogNeededOnClose
				isOpen={isOpenConfirmModal}
				onClose={closeConfirmModal}
				width={MODAL_WIDTH_580}
			>
				<Confirm
					isLoading={isLoadingConfirmModal}
					onClose={closeConfirmModal}
					onSubmit={submitConfirmHandler}
					question={confirmationText}
				>
					{subTextConfirmation}
				</Confirm>
			</Modal>

			{selectedNetworkElement && isOpenNetworkElementModal && (
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyForm}
					isLoading={isLoadingConfirmModal}
					isOpen
					onClose={closeNetworkElementModal}
					title='Edit network element'
					width={MODAL_WIDTH_580}
				>
					<EditNetworkElementsForm
						handleCheckIpAddress={handleCheckIpAddress}
						handleCheckName={handleCheckName}
						isLoadingForm={isLoadingConfirmModal}
						networkElement={selectedNetworkElement}
						onEditNetworkElement={onEditNetworkElement}
						setIsDirtyForm={setIsDirtyForm}
					/>
				</Modal>
			)}

			<ProfileTabSection
				currentTab={currentTab}
				getCurrentTab={getCurrentTab}
				maxWidth='unset'
				setCurrentTab={setCurrentTab}
				tabs={tabs}
			/>
		</div>
	);
};

export default ManageNetworkElements;
